import { IntelligenceTypes } from '@mono/proto-clients';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { intelligenceClient } from '../../../../lib/connect/clients';
const INTELLIGENCE_KEY = 'intelligence run';
// A run only needs review while it's in one of these states - everything
// earlier is still processing, everything later (failed/applied/cancelled/
// superseded) is done with review.
const AWAITING_REVIEW_STATES = [
    IntelligenceTypes.IntelligenceRunState.READY_FOR_REVIEW,
    IntelligenceTypes.IntelligenceRunState.PARTIALLY_READY
];
// Runs the awaiting-review list for a target, then hydrates each hit with its
// proposals. ListIntelligenceRuns deliberately omits proposals (it's a status
// board, not the review surface - see baltigo's ListIntelligenceRuns), so the
// review UI needs the per-run GetIntelligenceRun follow-up to have anything to
// render. Pangaea already orders runs by created_at DESC, so runs[0] is the
// most recent.
// Exported so a caller that changes what the awaiting-review list would return
// (applying a run, or watching one finish) can invalidate exactly that query
// instead of the whole INTELLIGENCE_KEY tree - a blanket invalidate would also
// reset the in-flight poll below and the per-run progress queries.
export function awaitingReviewQueryKey(workflowKind, target) {
    return [
        INTELLIGENCE_KEY,
        'awaiting-review',
        workflowKind,
        target.programId,
        target.entityType,
        target.entityId
    ];
}
export function useAwaitingReviewRuns(workflowKind, target) {
    return useQuery({
        queryKey: awaitingReviewQueryKey(workflowKind, target),
        enabled: !!target.programId,
        staleTime: 15000,
        queryFn: async ()=>{
            const listResp = await intelligenceClient.listIntelligenceRuns({
                workflowKind,
                target: {
                    programId: target.programId,
                    entityType: target.entityType,
                    entityId: target.entityId ?? ''
                },
                states: AWAITING_REVIEW_STATES
            });
            const hydrated = await Promise.all(listResp.runs.map((run)=>intelligenceClient.getIntelligenceRun({
                    runId: run.id
                })));
            return hydrated.map((resp)=>resp.run).filter((run)=>!!run);
        }
    });
}
// The states a run passes through before it can be reviewed. A run sitting in
// any of them is doing work: proposals for this target are coming, but are not
// here yet.
//
// This list is the complement of AWAITING_REVIEW_STATES and the terminal ones
// (FAILED/APPLIED_TO_DRAFT/CANCELLED/SUPERSEDED) - if a new pre-review state is
// ever added to IntelligenceRunState, add it here too, or the panel will fall
// silent during it again (SAD-154).
const IN_FLIGHT_RUN_STATES = [
    IntelligenceTypes.IntelligenceRunState.QUEUED,
    IntelligenceTypes.IntelligenceRunState.UPLOADING,
    IntelligenceTypes.IntelligenceRunState.CONVERTING,
    IntelligenceTypes.IntelligenceRunState.OCR,
    IntelligenceTypes.IntelligenceRunState.CLASSIFYING,
    IntelligenceTypes.IntelligenceRunState.EXTRACTING,
    IntelligenceTypes.IntelligenceRunState.RECONCILING
];
// Runs still working on this target, polled while any exist.
//
// SAD-154: starting a run used to leave the review panel rendering nothing at
// all until something else happened to refetch the awaiting-review list. Since
// StartIntelligenceRun returns the moment the run is STARTED (not finished),
// that "something else" was the user clicking Re-extract a second time - which
// also spawned a duplicate run. Watching the in-flight set gives the panel a
// visible processing state AND a completion edge to refetch on, so one click
// is enough.
//
// Deliberately a list query, not useIntelligenceRunProgress(runId): the panel
// must show progress for a run it did not itself start (another tab, a re-open
// of the form mid-run), and it has no run id to poll in those cases.
export function useInFlightRuns(workflowKind, target) {
    return useQuery({
        queryKey: [
            INTELLIGENCE_KEY,
            'in-flight',
            workflowKind,
            target.programId,
            target.entityType,
            target.entityId
        ],
        enabled: !!target.programId,
        // No staleTime: the whole point is to observe this set changing.
        // 2s matches useIntelligenceRunProgress; polling stops the moment the set
        // empties, so an idle form costs one request, not a heartbeat.
        refetchInterval: (query)=>(query.state.data?.length ?? 0) > 0 ? 2000 : false,
        queryFn: async ()=>{
            const resp = await intelligenceClient.listIntelligenceRuns({
                workflowKind,
                target: {
                    programId: target.programId,
                    entityType: target.entityType,
                    entityId: target.entityId ?? ''
                },
                states: IN_FLIGHT_RUN_STATES
            });
            return resp.runs;
        }
    });
}
// Exported for the same reason as awaitingReviewQueryKey above: ProposalReview's
// falling-edge effect needs to invalidate exactly this query when a run it was
// watching leaves the in-flight set - a run that just failed must not keep
// serving this query's cached "not failed yet" answer for the rest of its
// 15s staleTime, which is how a host with no other refetch trigger (SAD-321,
// SAD-154) went silent forever. Scoped the same way: a blanket invalidate of
// the whole INTELLIGENCE_KEY tree would also reset the in-flight poll.
export function failedRunQueryKey(workflowKind, target) {
    return [
        INTELLIGENCE_KEY,
        'failed',
        workflowKind,
        target.programId,
        target.entityType,
        target.entityId
    ];
}
// Runs the FAILED-state list for a target. ListIntelligenceRuns already
// returns state/warnings/error_message/failure_reason regardless of
// IncludeProposals - it's only the `proposals` field itself that the list
// view omits (see pangaea's intelligenceRunToProto) - and a failure notice
// never needs proposals, so no GetIntelligenceRun follow-up is needed here.
// Pangaea orders by created_at DESC, so runs[0] is the most recent.
//
// opts.enabled ANDs into the existing programId gate rather than replacing
// it - a caller that has pinned to a specific run (IntelligenceFailureNotice
// with a `pinnedRunId`) needs this target-scoped query fully inert, not just
// re-derived, since the shared target can carry a DIFFERENT host session's
// failed run that must never leak in as a fallback.
export function useFailedIntelligenceRun(workflowKind, target, opts) {
    return useQuery({
        queryKey: failedRunQueryKey(workflowKind, target),
        enabled: (opts?.enabled ?? true) && !!target.programId,
        staleTime: 15000,
        queryFn: async ()=>{
            const listResp = await intelligenceClient.listIntelligenceRuns({
                workflowKind,
                target: {
                    programId: target.programId,
                    entityType: target.entityType,
                    entityId: target.entityId ?? ''
                },
                states: [
                    IntelligenceTypes.IntelligenceRunState.FAILED
                ],
                pageSize: 1
            });
            // React Query forbids a queryFn resolving to undefined - null is the
            // defined "no failed run" value the component's `if (!run)` reads.
            return listResp.runs[0] ?? null;
        }
    });
}
// Fetches ONE run by id and yields it only when it is currently FAILED - the
// run-id-scoped counterpart to useFailedIntelligenceRun above. A host that
// pins to the exact run it started (e.g. DeviationsTab's
// pinnedDeviationRunId) cannot use the target-scoped list-and-take-[0] query:
// with pageSize 1, a genuinely failed pinned run would go missing the moment
// a NEWER failed run exists for the same shared target, and post-filtering
// the target's latest FAILED run on id match doesn't fix that - it would
// still hide the pinned run in exactly that case. Fetching by id sidesteps
// both failure modes. Same GetIntelligenceRun call useAwaitingReviewRuns
// already makes to hydrate a listed run.
export function useFailedIntelligenceRunById(runId) {
    return useQuery({
        queryKey: [
            INTELLIGENCE_KEY,
            'failed-by-id',
            runId
        ],
        enabled: !!runId,
        // A host pins the run it just started, and StartIntelligenceRun returns
        // while that run is still QUEUED - so "did it fail?" is a TRANSITION this
        // query has to observe, never a fact it can read once and cache. Without
        // the poll the first read (QUEUED) cached `null` for a run that failed a
        // moment later, and nothing refetched it: ProposalReview's falling edge
        // invalidates only the awaiting-review key, and refetchOnWindowFocus is
        // off globally. The dialog then showed NOTHING at all - no proposals (a
        // FAILED run is not awaiting review), no spinner (it left the in-flight
        // set), and no failure notice (this query still said null). Same
        // stop-when-settled shape as useIntelligenceRunProgress below.
        refetchInterval: (query)=>isRunSettled(query.state.data?.state) ? false : 2000,
        queryFn: async ()=>{
            const resp = await intelligenceClient.getIntelligenceRun({
                runId
            });
            // React Query forbids a queryFn resolving to undefined. The RAW run is
            // what lands in the cache - refetchInterval above reads its state to
            // decide when to stop - and `select` below is what narrows the value
            // callers see to the FAILED-only contract they already depend on.
            return resp.run ?? null;
        },
        select: (run)=>run?.state === IntelligenceTypes.IntelligenceRunState.FAILED ? run : null
    });
}
// Same retry-then-start two-step useReextractIntelligence performs for a
// prior failed run, but for a caller (IntelligenceFailureNotice) that already
// knows the failed run's id and doesn't need the artifact-id matching that
// hook does to decide between retrying and creating fresh.
export function useRetryIntelligenceRun() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: async (input)=>{
            await intelligenceClient.retryIntelligenceRun({
                runId: input.runId
            });
            const started = await intelligenceClient.startIntelligenceRun({
                runId: input.runId
            });
            return started.run;
        },
        // onSettled, not onSuccess: retryIntelligenceRun already committed the
        // FAILED -> QUEUED transition server-side by the time startIntelligenceRun
        // can fail, so a rejected mutation still leaves the cache serving a stale
        // FAILED run unless invalidation runs on both outcomes.
        onSettled: ()=>qc.invalidateQueries({
                queryKey: [
                    INTELLIGENCE_KEY
                ]
            })
    });
}
export function useReviewFieldProposal() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: async (input)=>{
            const resp = await intelligenceClient.reviewFieldProposal(input);
            return resp.proposal;
        },
        onSuccess: ()=>qc.invalidateQueries({
                queryKey: [
                    INTELLIGENCE_KEY
                ]
            })
    });
}
export function useBulkReviewFieldProposals() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: async (input)=>{
            const resp = await intelligenceClient.bulkReviewFieldProposals(input);
            return resp.run;
        },
        onSuccess: ()=>qc.invalidateQueries({
                queryKey: [
                    INTELLIGENCE_KEY
                ]
            })
    });
}
// Invalidates the awaiting-review query for the applied run's target.
//
// This USED to deliberately skip invalidation, so that the panel would keep
// rendering the run after apply rather than vanishing the instant it left
// READY_FOR_REVIEW. That kept the post-apply view alive, but it also left the
// applied run sitting in the cache: opening a *fresh* create form afterwards
// re-rendered the previous draft's whole review block (SAD-154, matt
// 2026-08-27 - "clicking the 'New CPARS Score' button automatically opens the
// previous forms 'Ready for review' section").
//
// The panel now keeps its own post-apply snapshot of the run (see
// ProposalReview's appliedSnapshot), so the applied view survives this
// invalidation while the cache no longer leaks the run into the next draft.
// Removing the snapshot without also restoring this skip would make the panel
// disappear at the moment of apply - the regression the old comment guarded.
//
// Scoped to the one query key rather than the INTELLIGENCE_KEY tree: a blanket
// invalidate would also restart the in-flight poll.
export function useMarkRunAppliedToDraft(workflowKind, target) {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: async (input)=>{
            const resp = await intelligenceClient.markRunAppliedToDraft(input);
            return resp.run;
        },
        onSuccess: ()=>qc.invalidateQueries({
                queryKey: awaitingReviewQueryKey(workflowKind, target)
            })
    });
}
// Same set, order-independent: a retried run's sourceArtifactIds came back
// from the server and isn't guaranteed to preserve the caller's original
// array order.
function sameArtifactIds(a, b) {
    return a.length === b.length && new Set(a).size === new Set(b).size && a.every((id)=>b.includes(id));
}
/** Creates (or retries) an IntelligenceRun and kicks off extraction. */ export function useReextractIntelligence() {
    const qc = useQueryClient();
    const mutation = useMutation({
        mutationFn: async (input)=>{
            const artifactIds = input.sourceArtifactIds && input.sourceArtifactIds.length > 0 ? input.sourceArtifactIds : input.sourceArtifactId ? [
                input.sourceArtifactId
            ] : [];
            if (artifactIds.length === 0) {
                throw new Error('Attach a source document before re-extracting.');
            }
            const listResp = await intelligenceClient.listIntelligenceRuns({
                workflowKind: input.workflowKind,
                target: {
                    programId: input.target.programId,
                    entityType: input.target.entityType,
                    entityId: input.target.entityId ?? ''
                },
                states: [
                    IntelligenceTypes.IntelligenceRunState.FAILED,
                    IntelligenceTypes.IntelligenceRunState.CANCELLED
                ],
                pageSize: 1
            });
            const priorFailed = listResp.runs[0];
            const priorSourceIds = priorFailed?.sourceArtifactIds ?? [];
            const canRetryPrior = !!priorFailed?.id && sameArtifactIds(priorSourceIds, artifactIds);
            let runId;
            if (canRetryPrior) {
                await intelligenceClient.retryIntelligenceRun({
                    runId: priorFailed.id
                });
                runId = priorFailed.id;
            } else {
                const created = await intelligenceClient.createIntelligenceRun({
                    workflowKind: input.workflowKind,
                    target: {
                        programId: input.target.programId,
                        entityType: input.target.entityType,
                        entityId: input.target.entityId ?? ''
                    },
                    sourceArtifactIds: artifactIds
                });
                runId = created.run?.id ?? '';
                if (!runId) throw new Error('Intelligence run was not created.');
            }
            const started = await intelligenceClient.startIntelligenceRun({
                runId
            });
            return started.run;
        },
        // onSettled, not onSuccess: on the canRetryPrior branch, retryIntelligenceRun
        // already committed the FAILED -> QUEUED transition server-side before
        // startIntelligenceRun can fail, so a rejected mutation still leaves the
        // cache stale unless invalidation runs on both outcomes.
        onSettled: ()=>qc.invalidateQueries({
                queryKey: [
                    INTELLIGENCE_KEY
                ]
            })
    });
    return {
        reextract: mutation.mutateAsync,
        running: mutation.isPending
    };
}
// A run is finished — for polling purposes — once it can no longer change on its
// own. READY_FOR_REVIEW and PARTIALLY_READY count: the work is done and what
// happens next is a human's move, not the pipeline's.
const SETTLED_RUN_STATES = [
    IntelligenceTypes.IntelligenceRunState.READY_FOR_REVIEW,
    IntelligenceTypes.IntelligenceRunState.PARTIALLY_READY,
    IntelligenceTypes.IntelligenceRunState.FAILED,
    IntelligenceTypes.IntelligenceRunState.CANCELLED,
    IntelligenceTypes.IntelligenceRunState.SUPERSEDED,
    IntelligenceTypes.IntelligenceRunState.APPLIED_TO_DRAFT
];
export function isRunSettled(state) {
    return state !== undefined && SETTLED_RUN_STATES.includes(state);
}
// STALE_AFTER_MS is how long a run may go without a progress update before it is
// treated as stopped rather than slow.
//
// Five minutes, against a worker that reports at every phase boundary: reading a
// sheet is seconds, and the ONE genuinely long step is a Datalab conversion,
// which reports before it starts. So five minutes of silence means something
// died — most often the worker, which cannot then write its own failure.
const STALE_AFTER_MS = 5 * 60 * 1000;
// runIsStale reports whether a still-running run has gone quiet past that bound.
//
// Deliberately NOT called "failed": we do not know that it failed, only that it
// has stopped telling us anything. That distinction is the whole point — the
// alternative is a card that claims progress it cannot see.
export function runIsStale(run) {
    if (!run || isRunSettled(run.state)) {
        return false;
    }
    const seconds = run.updatedAt?.seconds;
    if (!seconds) {
        // No timestamp is not evidence of staleness; keep watching.
        return false;
    }
    return Date.now() - Number(seconds) * 1000 > STALE_AFTER_MS;
}
// Polls ONE run while it is still moving, and stops the moment it settles.
//
// 2s: fast enough that a step lasting a few seconds is actually seen, slow
// enough that a 20-minute conversion is ~600 requests rather than 12,000. The
// interval returns false once settled, so a finished run costs nothing — a
// progress poll that keeps firing after completion is how these quietly become
// the busiest endpoint in the system.
export function useIntelligenceRunProgress(runId, opts) {
    return useQuery({
        queryKey: [
            INTELLIGENCE_KEY,
            'progress',
            runId
        ],
        enabled: (opts?.enabled ?? true) && Boolean(runId),
        // A run that is GONE — deleted, or from a wiped database — must not be
        // retried forever. Without this the query keeps failing, the card never gets
        // a state, and it sits at "Queued…" permanently: a watch list entry that
        // outlived the thing it was watching.
        retry: 1,
        refetchInterval: (query)=>isRunSettled(query.state.data?.state) ? false : 2000,
        queryFn: async ()=>{
            const resp = await intelligenceClient.getIntelligenceRun({
                runId
            });
            return resp.run;
        }
    });
}
// Asks a run to stop.
//
// Writes CANCELLED to the run row; the worker notices at its next phase
// boundary and aborts. Cooperative rather than instant on purpose — the run row
// is shared state, so a cancel works whichever replica is doing the work, which
// an in-memory cancel map cannot promise.
export function useCancelIntelligenceRun() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: async (runId)=>{
            const resp = await intelligenceClient.cancelIntelligenceRun({
                runId
            });
            return resp.run;
        },
        onSuccess: (_run, runId)=>{
            void qc.invalidateQueries({
                queryKey: [
                    INTELLIGENCE_KEY,
                    'progress',
                    runId
                ]
            });
        }
    });
}
